import React, { useState, useRef } from 'react';
import { Avatar, Box, Button, IconButton, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { ZoomIn, ZoomOut } from '@mui/icons-material';
import AvatarEditor from 'react-avatar-editor';
import { getFirestore, collection, addDoc, Timestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const CreateQuote = ({ open, onClose }) => {
  const [author, setAuthor] = useState('');
  const [text, setText] = useState('');
  const [role, setRole] = useState('');
  const [link, setLink] = useState('');
  const [linkText, setLinkText] = useState(''); 
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState('');
  const editorRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const db = getFirestore();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setImage(file);
    } else {
      alert('Please upload a valid image.');
    }
  };

  const handleImageUpload = async () => {
    if (!image) return imageUrl; 
    const canvas = editorRef.current.getImageScaledToCanvas().toDataURL();
    const storage = getStorage();
    const storageRef = ref(storage, `quote-images/${Date.now()}_${image.name}`);
    const blob = await (await fetch(canvas)).blob();
    await uploadBytes(storageRef, blob);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const handleSubmit = async () => {
    if (!author || !text || !image) {
      setError('Author, Quote, and Image are required.');
      return;
    }

    try {
      const uploadedImageUrl = await handleImageUpload();
      await addDoc(collection(db, 'quotes'), {
        author,
        text,
        role,
        imageUrl: uploadedImageUrl,
        link,
        linkText,
        createdAt: Timestamp.now(),
      });
      setAuthor('');
      setText('');
      setRole('');
      setLink('');
      setLinkText('');
      setImage(null);
      setImageUrl('');
      setError('');
      onClose(); // Close dialog after creation
    } catch (err) {
      console.error('Error creating quote:', err);
      setError('An error occurred while creating the quote.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Create Quote</DialogTitle>
      <DialogContent>
        <TextField
          label="Author *"
          variant="outlined"
          fullWidth
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Role"
          variant="outlined"
          fullWidth
          value={role}
          onChange={(e) => setRole(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Quote *"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Link URL (optional)"
          variant="outlined"
          fullWidth
          value={link}
          onChange={(e) => setLink(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Link Text (optional)"
          variant="outlined"
          fullWidth
          value={linkText}
          onChange={(e) => setLinkText(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box mb={2}>
          <Button variant="outlined" component="label">
            Upload Image *
            <input type="file" accept="image/*" hidden onChange={handleFileChange} />
          </Button>
        </Box>
        {image && (
          <Box mb={2} textAlign="center">
            <AvatarEditor
              ref={editorRef}
              image={image}
              width={150}
              height={150}
              border={50}
              borderRadius={75} 
              scale={zoom}
              rotate={0}
            />
            <Box mt={2} display="flex" justifyContent="center">
              <IconButton onClick={() => setZoom(zoom + 0.1)}>
                <ZoomIn />
              </IconButton>
              <IconButton onClick={() => setZoom(zoom - 0.1)}>
                <ZoomOut />
              </IconButton>
            </Box>
          </Box>
        )}
        {error && <Typography color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create Quote
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateQuote;
